import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import * as Yup from "yup";

// Core reports a default that was never filled in as start 0 and length 1. That pair covers only the invoice
// month, which Core never stores, so it can only mean "not set" and is shown as empty fields.
export const toDefaultPeriodisationFormValue = (
  periodisation?: WintSuperkollAPIModel.IncomingInvoiceSupplierPeriodisation
): InvoiceFormTypes.RecurringPeriodisation | undefined => {
  if (!periodisation) return undefined;
  const neverSet = !periodisation.enabled && periodisation.startOffset === 0 && periodisation.length === 1;
  return {
    isEnabled: periodisation.enabled,
    from: neverSet ? undefined : periodisation.startOffset,
    duration: neverSet ? undefined : periodisation.length,
  };
};

// The number inputs emit their raw text, so a cleared field or a lone minus would otherwise fail as "not a number"
// even while the setting is off.
const emptyNumberInputToUndefined = (value: number, original: unknown) =>
  original === "" || original === "-" ? undefined : value;

// Must agree with the API, which refuses an invalid range only while the default is switched on.
export const defaultPeriodisationSchema = Yup.object<InvoiceFormTypes.RecurringPeriodisation>({
  isEnabled: Yup.boolean(),
  from: Yup.number()
    .transform(emptyNumberInputToUndefined)
    .when("isEnabled", ([isEnabled], schema: Yup.NumberSchema) =>
      isEnabled
        ? schema
            .required(TranslationKeys.Validations.Required)
            .min(-3, TranslationKeys.Supplier.DefaultPeriodisationInvalid)
            .max(24, TranslationKeys.Supplier.DefaultPeriodisationInvalid)
        : schema
    ),
  duration: Yup.number()
    .transform(emptyNumberInputToUndefined)
    .when("isEnabled", ([isEnabled], schema: Yup.NumberSchema) =>
      isEnabled
        ? schema
            .required(TranslationKeys.Validations.Required)
            .min(1, TranslationKeys.Supplier.DefaultPeriodisationInvalid)
            .max(12, TranslationKeys.Supplier.DefaultPeriodisationInvalid)
            .test(
              "endWithinWindow",
              TranslationKeys.Supplier.DefaultPeriodisationInvalid,
              (duration, context) => Number(context.parent.from ?? 0) + Number(duration ?? 1) - 1 <= 24
            )
            .test(
              "moreThanInvoiceMonth",
              TranslationKeys.Supplier.DefaultPeriodisationInvalid,
              (duration, context) => !(Number(context.parent.from ?? 0) === 0 && Number(duration ?? 1) === 1)
            )
        : schema
    ),
}).default(undefined);

// NumberInput shows only a red border, so the save-failed toast is the one place the rule can reach the user.
export const getDefaultPeriodisationSaveFailedMessage = (
  value?: InvoiceFormTypes.RecurringPeriodisation
): string | undefined => {
  try {
    defaultPeriodisationSchema.validateSync(value, { abortEarly: false });
    return undefined;
  } catch (error) {
    const messages = error instanceof Yup.ValidationError ? error.errors : [];
    return messages.includes(TranslationKeys.Supplier.DefaultPeriodisationInvalid)
      ? TranslationKeys.Supplier.DefaultPeriodisationSaveFailed
      : undefined;
  }
};
